import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input, Button } from '@klaro7/ui';

import { extractErrorMessage } from '@/lib/api';
import {
  useGoogleAdsSettings,
  useUpdateGoogleAdsSettings,
  useMetaPixelSettings,
  useUpdateMetaPixelSettings,
} from '@/features/settings/hooks/useServicesSettings';
import type { GoogleAdsSettings, MetaPixelSettings } from '@/features/settings/services/settings.service';

const GOOGLE_TAG_ID_PATTERN = /^AW-\d+$/;
/** Etichetta conversione: la parte dopo la barra nel send_to di Google. */
const GOOGLE_CONVERSION_LABEL_PATTERN = /^[A-Za-z0-9_-]+$/;
const META_PIXEL_ID_PATTERN = /^\d{5,20}$/;

function GoogleAdsCard() {
  const { data: settings, isLoading } = useGoogleAdsSettings(true);
  const updateMutation = useUpdateGoogleAdsSettings();

  const [values, setValues] = useState<GoogleAdsSettings>({
    enabled: false,
    tagId: '',
    purchaseLabel: '',
  });

  useEffect(() => {
    if (settings) setValues(settings);
  }, [settings]);

  const trimmedTagId = values.tagId.trim();
  const trimmedLabel = (values.purchaseLabel ?? '').trim();
  const tagIdInvalid = trimmedTagId !== '' && !GOOGLE_TAG_ID_PATTERN.test(trimmedTagId);
  const labelInvalid = trimmedLabel !== '' && !GOOGLE_CONVERSION_LABEL_PATTERN.test(trimmedLabel);
  const enabledWithoutTagId = values.enabled && trimmedTagId === '';

  async function handleSave() {
    if (tagIdInvalid) {
      toast.error('ID non valido: formato atteso AW-123456789');
      return;
    }
    if (labelInvalid) {
      toast.error('Etichetta conversione non valida: solo lettere, cifre, trattini e underscore');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        enabled: values.enabled,
        tagId: trimmedTagId,
        purchaseLabel: trimmedLabel,
      });
      toast.success('Tag Google Ads salvato con successo');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">Google Ads (gtag.js)</h3>
        <p className="text-xs text-neutral-600">
          Inserisci l'ID conversione di Google Ads. Lo script viene aggiunto
          automaticamente: non serve incollare lo snippet HTML.
        </p>
      </div>

      {isLoading ? (
        <div className="p-5 text-sm text-neutral-500">Caricamento…</div>
      ) : (
        <div className="space-y-4 p-5">
          <label className="flex items-center gap-2 text-sm text-neutral-800">
            <input
              type="checkbox"
              checked={values.enabled}
              onChange={(e) => setValues((prev) => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
              aria-label="Attiva tag Google Ads"
            />
            Attivo
          </label>

          <div className="max-w-md">
            <label className="mb-1 block text-sm font-medium text-neutral-800" htmlFor="google-ads-tag-id">
              Tag ID
            </label>
            <Input
              id="google-ads-tag-id"
              placeholder="AW-123456789"
              value={values.tagId}
              onChange={(e) => setValues((prev) => ({ ...prev, tagId: e.target.value }))}
            />
            {tagIdInvalid && (
              <p className="mt-1 text-xs text-red-600">
                Formato non valido: deve essere AW- seguito da sole cifre (es. AW-123456789)
              </p>
            )}
            {enabledWithoutTagId && (
              <p className="mt-1 text-xs text-amber-600">
                Inserisci l'ID per attivare il tag: senza ID non viene caricato nulla
              </p>
            )}
          </div>

          <div className="max-w-md">
            <label
              className="mb-1 block text-sm font-medium text-neutral-800"
              htmlFor="google-ads-purchase-label"
            >
              Etichetta conversione "Acquisto"
            </label>
            <Input
              id="google-ads-purchase-label"
              placeholder="XOU_CJ-wkN8cEM-d9qFE"
              value={values.purchaseLabel ?? ''}
              onChange={(e) => setValues((prev) => ({ ...prev, purchaseLabel: e.target.value }))}
            />
            <p className="mt-1 text-xs text-neutral-500">
              Solo la parte dopo la barra che Google mostra nello snippet
              (AW-123456789/<span className="font-medium">ETICHETTA</span>). Vuoto = nessuna
              conversione inviata. L'evento parte a ogni ricarica andata a buon fine, con
              importo e id del pagamento.
            </p>
            {labelInvalid && (
              <p className="mt-1 text-xs text-red-600">
                Formato non valido: sono ammessi solo lettere, cifre, trattini e underscore
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end border-t border-neutral-100 p-5">
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Salva
        </Button>
      </div>
    </div>
  );
}

function MetaPixelCard() {
  const { data: settings, isLoading } = useMetaPixelSettings(true);
  const updateMutation = useUpdateMetaPixelSettings();

  const [values, setValues] = useState<MetaPixelSettings>({
    enabled: false,
    pixelId: '',
    // Default finché il fetch non popola i valori reali: tutti gli eventi ON.
    events: { pageView: true, completeRegistration: true, purchase: true },
  });

  useEffect(() => {
    if (settings) setValues(settings);
  }, [settings]);

  const trimmedPixelId = values.pixelId.trim();
  const pixelIdInvalid = trimmedPixelId !== '' && !META_PIXEL_ID_PATTERN.test(trimmedPixelId);
  const enabledWithoutPixelId = values.enabled && trimmedPixelId === '';

  async function handleSave() {
    if (pixelIdInvalid) {
      toast.error('Pixel ID non valido: solo cifre (es. 2178436376339517)');
      return;
    }
    try {
      await updateMutation.mutateAsync({ enabled: values.enabled, pixelId: trimmedPixelId, events: values.events });
      toast.success('Meta Pixel salvato con successo');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">Meta Pixel (Facebook/Instagram)</h3>
        <p className="text-xs text-neutral-600">
          Inserisci il Pixel ID numerico da Gestione Eventi di Meta. Lo script e
          gli eventi (PageView, registrazione, prima ricarica) sono gestiti
          automaticamente: non serve incollare lo snippet HTML.
        </p>
      </div>

      {isLoading ? (
        <div className="p-5 text-sm text-neutral-500">Caricamento…</div>
      ) : (
        <div className="space-y-4 p-5">
          <label className="flex items-center gap-2 text-sm text-neutral-800">
            <input
              type="checkbox"
              checked={values.enabled}
              onChange={(e) => setValues((prev) => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
              aria-label="Attiva Meta Pixel"
            />
            Attivo
          </label>

          <div className="max-w-md">
            <label className="mb-1 block text-sm font-medium text-neutral-800" htmlFor="meta-pixel-id">
              Pixel ID
            </label>
            <Input
              id="meta-pixel-id"
              placeholder="2178436376339517"
              value={values.pixelId}
              onChange={(e) => setValues((prev) => ({ ...prev, pixelId: e.target.value }))}
            />
            {pixelIdInvalid && (
              <p className="mt-1 text-xs text-red-600">
                Formato non valido: solo cifre (es. 2178436376339517)
              </p>
            )}
            {enabledWithoutPixelId && (
              <p className="mt-1 text-xs text-amber-600">
                Inserisci il Pixel ID per attivare il tracciamento: senza ID non viene caricato nulla
              </p>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-neutral-800">Eventi tracciati</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={values.events.pageView}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, events: { ...prev.events, pageView: e.target.checked } }))
                  }
                  className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                  aria-label="Traccia PageView"
                />
                PageView (visite pagina)
              </label>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={values.events.completeRegistration}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      events: { ...prev.events, completeRegistration: e.target.checked },
                    }))
                  }
                  className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                  aria-label="Traccia registrazione completata"
                />
                Registrazione completata
              </label>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={values.events.purchase}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, events: { ...prev.events, purchase: e.target.checked } }))
                  }
                  className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                  aria-label="Traccia prima ricarica"
                />
                Prima ricarica (acquisto)
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end border-t border-neutral-100 p-5">
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Salva
        </Button>
      </div>
    </div>
  );
}

export function AdminTrackingPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Tracking</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Tag di tracciamento caricati sulla piattaforma. Partono solo per i visitatori
          che accettano i cookie di profilazione.
        </p>
      </div>

      <GoogleAdsCard />
      <MetaPixelCard />
    </div>
  );
}
